(() => {
  if (window.themeCartSubscriptionSwapInitialized) return;
  window.themeCartSubscriptionSwapInitialized = true;

  const swapRootSelector = '[data-subscription-swap]';
  const giftRootSelector = '[data-subscription-gift]';
  const analyticsAttribute = '__subscription_upgrade_source';

  const getSectionsToUpdate = () => {
    const sectionsToUpdate = [];
    const pageHeader = document.querySelector('page-header');
    const cartDrawer = document.querySelector('cart-drawer');
    const pageCartForm = Array.from(document.querySelectorAll('cart-form[data-ajax-update="true"]'))
      .find((el) => !el.closest('cart-drawer'));

    if (pageHeader?.dataset.sectionId && typeof pageHeader.updateFromCartChange === 'function') {
      sectionsToUpdate.push({
        sectionId: pageHeader.dataset.sectionId,
        apply: (html) => pageHeader.updateFromCartChange(html)
      });
    }

    if (cartDrawer?.dataset.sectionId && typeof cartDrawer.updateFromCartChange === 'function') {
      sectionsToUpdate.push({
        sectionId: cartDrawer.dataset.sectionId,
        apply: (html) => cartDrawer.updateFromCartChange(html)
      });
    }

    if (pageCartForm?.dataset.sectionId && typeof pageCartForm.refreshFromHtml === 'function') {
      sectionsToUpdate.push({
        sectionId: pageCartForm.dataset.sectionId,
        apply: (html) => pageCartForm.refreshFromHtml(html)
      });
    }

    return sectionsToUpdate;
  };

  const buildPayloadWithSections = (payload, sectionsToUpdate) => {
    if (!sectionsToUpdate.length) return payload;

    return {
      ...payload,
      sections: sectionsToUpdate.map(({ sectionId }) => sectionId).join(','),
      sections_url: `${window.location.pathname}${window.location.search}`
    };
  };

  const applyBundledSections = (sectionsToUpdate, data) => {
    if (!sectionsToUpdate.length || !data?.sections) return false;

    const hasAllSections = sectionsToUpdate.every(({ sectionId }) => typeof data.sections[sectionId] === 'string');
    if (!hasAllSections) return false;

    sectionsToUpdate.forEach(({ sectionId, apply }) => {
      apply(data.sections[sectionId]);
    });

    return true;
  };

  const setFeedback = (root, selector, message, isError = false) => {
    const feedback = root.querySelector(selector);
    if (!feedback) return;

    if (!message) {
      feedback.hidden = true;
      feedback.textContent = '';
      feedback.classList.remove('errors');
      return;
    }

    feedback.hidden = false;
    feedback.textContent = message;
    feedback.classList.toggle('errors', isError);
  };

  const setSwapLoadingState = (root, isLoading) => {
    const checkbox = root.querySelector('[data-subscription-swap-checkbox]');
    if (!checkbox) return;

    checkbox.disabled = isLoading;
    checkbox.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
  };

  const setGiftLoadingState = (root, isLoading) => {
    const button = root.querySelector('[data-subscription-gift-button]');
    const select = root.querySelector('[data-subscription-gift-select]');

    if (button) {
      button.disabled = isLoading || !select || !select.value;
      button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
      button.classList.toggle('is-loading', isLoading);
    }

    if (select) {
      select.disabled = isLoading;
    }
  };

  const updateCart = async (payload, sectionsToUpdate = []) => {
    const response = await fetch(theme.routes.cartUpdate, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(buildPayloadWithSections(payload, sectionsToUpdate))
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(typeof data.description === 'string' ? data.description : 'We could not update your cart.');
    }

    return data;
  };

  const addItems = async (items, sectionsToUpdate = []) => {
    const response = await fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(buildPayloadWithSections({ items }, sectionsToUpdate))
    });

    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(typeof data.description === 'string' ? data.description : 'We could not add the subscription.');
    }

    return data;
  };

  document.addEventListener('change', async (event) => {
    const swapCheckbox = event.target.closest('[data-subscription-swap-checkbox]');
    if (swapCheckbox) {
      const root = swapCheckbox.closest(swapRootSelector);
      if (!root || !swapCheckbox.checked) return;

      const offerType = root.dataset.offerType;
      const enableGiftFeature = root.dataset.enableGiftFeature === 'true';
      const sourceIssueTitle = root.dataset.sourceIssueTitle || 'Single issue';
      const lineKey = root.dataset.lineKey;
      const lineQuantity = parseInt(root.dataset.lineQuantity, 10) || 0;
      const subscriptionVariantId = parseInt(root.dataset.subscriptionVariantId, 10);
      const sellingPlanId = parseInt(root.dataset.sellingPlanId, 10);
      const sectionsToUpdate = getSectionsToUpdate();

      setFeedback(root, '[data-subscription-swap-feedback]', '');
      setSwapLoadingState(root, true);

      try {
        await addItems([
          {
            id: subscriptionVariantId,
            quantity: 1,
            selling_plan: sellingPlanId
          }
        ]);

        let cartUpdateResponse;

        if (offerType === 'current') {
          cartUpdateResponse = await updateCart({
            updates: {
              [lineKey]: Math.max(lineQuantity - 1, 0)
            },
            attributes: {
              [analyticsAttribute]: `Cart single issue upgrade: ${sourceIssueTitle} (current issue)`
            }
          }, sectionsToUpdate);
        } else {
          cartUpdateResponse = await updateCart({
            attributes: {
              [analyticsAttribute]: `Cart single issue upgrade: ${sourceIssueTitle} (archive issue)`
            }
          }, sectionsToUpdate);
        }

        if (!applyBundledSections(sectionsToUpdate, cartUpdateResponse)) {
          document.dispatchEvent(
            new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
          );
        }
      } catch (error) {
        swapCheckbox.checked = false;
        setFeedback(root, '[data-subscription-swap-feedback]', error.message || 'We could not update your cart.', true);
        setSwapLoadingState(root, false);
      }
      return;
    }

    const giftSelect = event.target.closest('[data-subscription-gift-select]');
    if (!giftSelect) return;

    const giftRoot = giftSelect.closest(giftRootSelector);
    if (!giftRoot) return;

    setFeedback(giftRoot, '[data-subscription-gift-feedback]', '');

    const giftButton = giftRoot.querySelector('[data-subscription-gift-button]');
    if (giftButton) {
      giftButton.disabled = !giftSelect.value;
      giftButton.setAttribute('aria-disabled', giftButton.disabled ? 'true' : 'false');
    }
  });

  document.addEventListener('click', async (event) => {
    const giftButton = event.target.closest('[data-subscription-gift-button]');
    if (!giftButton) return;

    const root = giftButton.closest(giftRootSelector);
    if (!root) return;

    event.preventDefault();

    const select = root.querySelector('[data-subscription-gift-select]');
    const selectedVariantId = parseInt(select?.value, 10);

    if (!selectedVariantId) {
      setFeedback(root, '[data-subscription-gift-feedback]', 'Select an archive issue first.', true);
      return;
    }

    setFeedback(root, '[data-subscription-gift-feedback]', '');
    setGiftLoadingState(root, true);

    try {
      const sectionsToUpdate = getSectionsToUpdate();
      const addResponse = await addItems([
        {
          id: selectedVariantId,
          quantity: 1
        }
      ], sectionsToUpdate);

      if (!applyBundledSections(sectionsToUpdate, addResponse)) {
        document.dispatchEvent(
          new CustomEvent('on:cart:change', { bubbles: true, cancelable: false })
        );
      }
    } catch (error) {
      setFeedback(root, '[data-subscription-gift-feedback]', error.message || 'We could not add the archive issue.', true);
      setGiftLoadingState(root, false);
    }
  });
})();
